import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Save, Eye, EyeOff, CreditCard, Mail, Settings, Phone, Globe, Repeat } from "lucide-react";
import { toast } from "sonner";

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [rawSettings, setRawSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [showResendKey, setShowResendKey] = useState(false);

  // Editable fields
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [mbwayEnabled, setMbwayEnabled] = useState(true);
  const [stripeApiKey, setStripeApiKey] = useState("");
  const [emailProvider, setEmailProvider] = useState("resend");
  const [resendApiKey, setResendApiKey] = useState("");
  const [senderEmail, setSenderEmail] = useState("onboarding@resend.dev");
  const [googleReviewUrl, setGoogleReviewUrl] = useState("");
  const [followupEmailEnabled, setFollowupEmailEnabled] = useState(true);
  const [subscriptionPrice, setSubscriptionPrice] = useState(30);
  const [subscriptionCredits, setSubscriptionCredits] = useState(2);
  const [subscriptionsPublicEnabled, setSubscriptionsPublicEnabled] = useState(true);
  const [frontendUrl, setFrontendUrl] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [maskedRes, rawRes] = await Promise.all([
        axios.get(`${API}/admin/settings`, { withCredentials: true }),
        axios.get(`${API}/admin/settings/raw`, { withCredentials: true }),
      ]);
      setSettings(maskedRes.data);
      setRawSettings(rawRes.data);
      setStripeEnabled(rawRes.data.stripe_enabled ?? true);
      setMbwayEnabled(rawRes.data.mbway_enabled ?? true);
      setStripeApiKey(rawRes.data.stripe_api_key || "");
      setEmailProvider(rawRes.data.email_provider || "resend");
      setResendApiKey(rawRes.data.resend_api_key || "");
      setSenderEmail(rawRes.data.sender_email || "onboarding@resend.dev");
      setGoogleReviewUrl(rawRes.data.google_review_url || "");
      setFollowupEmailEnabled(rawRes.data.followup_email_enabled ?? true);
      setSubscriptionPrice(rawRes.data.subscription_default_price ?? 30);
      setSubscriptionCredits(rawRes.data.subscription_default_weekly_credits ?? 2);
      setSubscriptionsPublicEnabled(rawRes.data.subscriptions_public_enabled ?? true);
      setFrontendUrl(rawRes.data.frontend_url || "");
    } catch {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/settings`, {
        stripe_enabled: stripeEnabled,
        mbway_enabled: mbwayEnabled,
        stripe_api_key: stripeApiKey,
        email_provider: emailProvider,
        resend_api_key: resendApiKey,
        sender_email: senderEmail,
        google_review_url: googleReviewUrl,
        followup_email_enabled: followupEmailEnabled,
        subscription_default_price: subscriptionPrice,
        subscription_default_weekly_credits: subscriptionCredits,
        subscriptions_public_enabled: subscriptionsPublicEnabled,
        frontend_url: frontendUrl,
      }, { withCredentials: true });
      toast.success("Configurações guardadas!");
      fetchSettings();
    } catch {
      toast.error("Erro ao guardar configurações");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#3d5a63] mb-2">Configurações</h1>
        <p className="text-[#475569]">Gere as chaves API e configurações da aplicação</p>
      </div>

      {/* Domain Settings - IMPORTANT */}
      <Card className="bg-white border-0 shadow-sm rounded-xl border-l-4 border-l-[#6697a4]" data-testid="domain-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#3d5a63]">
            <Globe className="h-5 w-5" /> Domínio da Aplicação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>URL do Frontend (Domínio)</Label>
            <Input 
              value={frontendUrl} 
              onChange={(e) => setFrontendUrl(e.target.value)} 
              placeholder="https://apuliasurfschool.com" 
              data-testid="frontend-url" 
            />
            <p className="text-xs text-[#94A3B8] mt-1">
              O domínio usado nos links de autenticação (magic links) enviados por email.
              <br />
              <span className="text-amber-600">Deixa em branco para usar o domínio atual do ambiente.</span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Payment Settings */}
      <Card className="bg-white border-0 shadow-sm rounded-xl" data-testid="payment-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#3d5a63]">
            <CreditCard className="h-5 w-5" /> Pagamentos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Stripe (Cartão)</Label>
              <p className="text-sm text-[#94A3B8]">Aceitar pagamentos com cartão de crédito/débito</p>
            </div>
            <Switch checked={stripeEnabled} onCheckedChange={setStripeEnabled} data-testid="toggle-stripe" />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">MBWay</Label>
              <p className="text-sm text-[#94A3B8]">Aceitar pagamentos via MBWay</p>
            </div>
            <Switch checked={mbwayEnabled} onCheckedChange={setMbwayEnabled} data-testid="toggle-mbway" />
          </div>

          <div>
            <Label>Stripe API Key</Label>
            <div className="flex gap-2">
              <Input
                type={showStripeKey ? "text" : "password"}
                value={stripeApiKey}
                onChange={(e) => setStripeApiKey(e.target.value)}
                placeholder="sk_live_..."
                data-testid="stripe-api-key"
              />
              <Button variant="outline" size="icon" onClick={() => setShowStripeKey(!showStripeKey)}>
                {showStripeKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-[#94A3B8] mt-1">A chave Stripe para processar pagamentos</p>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Settings */}
      <Card className="bg-white border-0 shadow-sm rounded-xl" data-testid="subscription-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#3d5a63]">
            <Repeat className="h-5 w-5" /> Mensalidades / Subscrições
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Subscrições Públicas</Label>
              <p className="text-sm text-[#94A3B8]">Permitir que alunos vejam e subscrevam a mensalidades</p>
            </div>
            <Switch 
              checked={subscriptionsPublicEnabled} 
              onCheckedChange={setSubscriptionsPublicEnabled} 
              data-testid="toggle-subscriptions-public" 
            />
          </div>
          
          {!subscriptionsPublicEnabled && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                ⚠️ As mensalidades estão ocultas. Os alunos não podem ver nem subscrever na landing page.
                <br />
                <span className="text-xs">Apenas admins podem atribuir mensalidades manualmente.</span>
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Preço Mensal Padrão (€)</Label>
              <Input 
                type="number" 
                value={subscriptionPrice} 
                onChange={(e) => setSubscriptionPrice(parseFloat(e.target.value) || 0)} 
                data-testid="subscription-price" 
              />
            </div>
            <div>
              <Label>Créditos Semanais Padrão</Label>
              <Input 
                type="number" 
                value={subscriptionCredits} 
                onChange={(e) => setSubscriptionCredits(parseInt(e.target.value) || 0)} 
                data-testid="subscription-credits" 
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card className="bg-white border-0 shadow-sm rounded-xl" data-testid="email-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#3d5a63]">
            <Mail className="h-5 w-5" /> Email (Lembretes)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Provedor de Email</Label>
            <Select value={emailProvider} onValueChange={setEmailProvider}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="resend">Resend</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Resend API Key</Label>
            <div className="flex gap-2">
              <Input
                type={showResendKey ? "text" : "password"}
                value={resendApiKey}
                onChange={(e) => setResendApiKey(e.target.value)}
                placeholder="re_..."
                data-testid="resend-api-key"
              />
              <Button variant="outline" size="icon" onClick={() => setShowResendKey(!showResendKey)}>
                {showResendKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-[#94A3B8] mt-1">Obtém em <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-[#6697a4] underline">resend.com</a></p>
          </div>

          <div>
            <Label>Email Remetente</Label>
            <Input value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} placeholder="noreply@escola.com" data-testid="sender-email" />
            <p className="text-xs text-[#94A3B8] mt-1">Endereço de email que aparece como remetente</p>
          </div>

          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label className="text-base">Email Follow-up (Google Review)</Label>
                <p className="text-sm text-[#94A3B8]">Enviar email pós-aula a pedir review no Google</p>
              </div>
              <Switch checked={followupEmailEnabled} onCheckedChange={setFollowupEmailEnabled} data-testid="toggle-followup" />
            </div>

            <div>
              <Label>Link Google Reviews</Label>
              <Input 
                value={googleReviewUrl} 
                onChange={(e) => setGoogleReviewUrl(e.target.value)} 
                placeholder="https://www.google.com/maps/place/..." 
                data-testid="google-review-url" 
              />
              <p className="text-xs text-[#94A3B8] mt-1">Link direto para os alunos deixarem review no Google Maps</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <Button onClick={handleSave} disabled={saving} className="w-full bg-[#6697a4] hover:bg-[#3d5a63] py-6 text-base" data-testid="save-settings-btn">
        <Save className="mr-2 h-5 w-5" /> {saving ? "A guardar..." : "Guardar Configurações"}
      </Button>
    </div>
  );
};

export default AdminSettings;
